import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Json } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  addTagToBidders,
  broadcastToSegment,
  createBidderSegment,
  deleteBidderSegment,
  removeTagFromBidders,
  resolveSegmentMemberIds,
  supabaseCrm,
  updateBidderSegment,
  type BidderLifetimeStatsRow,
  type BidderSegmentRow,
} from '../../lib/supabase-crm';
import { useSession } from '../../hooks/useSession';
import type React from 'react';

type ProfileLite = {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
};

const PAGE_SIZE = 25;
const STATUSES = ['active', 'pending', 'suspended', 'archived'] as const;

/**
 * /admin/bidders — tenant-scoped Bidder CRM list page. Module #21 Chunk 3.
 *
 * Filters: status, search (display_name / first_name / last_name), tag,
 * saved segment, min spend, days-since-last-bid threshold.
 *
 * Data source: public.bidder_lifetime_stats view (Module #21 Chunk 1) for
 * LTV + derived stats; public.profiles for display name / phone fanout.
 * RLS auto-scopes both to the caller's tenant.
 *
 * Detail page lands at /admin/bidders/:id in Chunk 4. Bulk-action UI
 * (tag add/remove, CSV export) lands in Chunk 5.
 */
export function AdminBidders() {
  const [rows, setRows] = useState<BidderLifetimeStatsRow[]>([]);
  const [profiles, setProfiles] = useState<Map<string, ProfileLite>>(new Map());
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [segmentFilter, setSegmentFilter] = useState<string>('');
  const [minSpendDollars, setMinSpendDollars] = useState<string>('');
  const [minDaysInactive, setMinDaysInactive] = useState<string>('');
  const [page, setPage] = useState(0);

  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [segments, setSegments] = useState<BidderSegmentRow[]>([]);

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [bulkTagOpen, setBulkTagOpen] = useState<'add' | 'remove' | null>(null);
  const [segmentBuilderOpen, setSegmentBuilderOpen] = useState<BidderSegmentRow | 'new' | null>(
    null,
  );
  const [broadcastOpen, setBroadcastOpen] = useState<BidderSegmentRow | null>(null);

  const { session } = useSession();
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // Clear selection whenever filters or page changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, tagFilter, segmentFilter, minSpendDollars, minDaysInactive, search, page]);

  // Tenant scope for "New segment" — derived from the first row visible. If
  // no rows are visible, segment creation needs a different signal — but in
  // practice the page is always loaded inside a tenant's admin app.
  const visibleTenantId = useMemo(() => rows[0]?.tenant_id ?? null, [rows]);

  // ─── CSV export of current filtered set ─────────────────────────────────
  const exportCsv = useCallback((): void => {
    const headers = [
      'bidder_number',
      'name',
      'status',
      'total_bids',
      'total_spend_dollars',
      'last_bid_at',
      'days_since_last_bid',
      'tags',
    ];
    const escape = (val: string): string => {
      if (/[",\n]/.test(val)) return `"${val.replace(/"/g, '""')}"`;
      return val;
    };
    const lines = [headers.join(',')];
    for (const row of rows) {
      const p = profiles.get(row.user_id);
      const name =
        p?.display_name ?? [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim() ?? '';
      lines.push(
        [
          row.tenant_bidder_number != null ? String(row.tenant_bidder_number) : '',
          escape(name),
          row.status,
          String(row.total_bids),
          (row.total_spend_cents / 100).toFixed(2),
          row.last_bid_at ?? '',
          row.days_since_last_bid != null ? String(row.days_since_last_bid) : '',
          escape((row.tenant_tags ?? []).join('; ')),
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bidders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, profiles]);

  // ─── Selection toggles ──────────────────────────────────────────────────
  const toggleRow = useCallback((id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback((): void => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return prev;
      const allOnPage = rows.every((r) => prev.has(r.tenant_buyer_id));
      const next = new Set(prev);
      for (const r of rows) {
        if (allOnPage) next.delete(r.tenant_buyer_id);
        else next.add(r.tenant_buyer_id);
      }
      return next;
    });
  }, [rows]);

  const allOnPageSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r.tenant_buyer_id)),
    [rows, selectedIds],
  );

  // ─── Debounce search input ──────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Load segment + tag option lists once on mount ──────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [segRes, tagRes] = await Promise.all([
        supabaseCrm
          .from('bidder_segments')
          .select('id, tenant_id, name, description, filter_definition')
          .order('name'),
        supabaseCrm
          .from('bidder_lifetime_stats')
          .select('tenant_tags')
          .not('tenant_tags', 'is', null),
      ]);
      if (cancelled) return;

      setSegments(((segRes.data ?? []) as unknown as BidderSegmentRow[]) ?? []);

      const seen = new Set<string>();
      const tagRows = (tagRes.data ?? []) as unknown as Array<{ tenant_tags: string[] | null }>;
      for (const row of tagRows) {
        for (const tag of row.tenant_tags ?? []) {
          if (tag) seen.add(tag);
        }
      }
      setAvailableTags([...seen].sort((a, b) => a.localeCompare(b)));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Load page of bidders + matching profiles ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // Step 1: optional segment → resolve member ids
      let segmentMemberIds: string[] = [];
      let hasSegmentFilter = false;
      if (segmentFilter) {
        hasSegmentFilter = true;
        const { data, error: rpcErr } = await resolveSegmentMemberIds(segmentFilter);
        if (cancelled) return;
        if (rpcErr) {
          setError(rpcErr.message);
          setRows([]);
          setTotalCount(0);
          setLoading(false);
          return;
        }
        segmentMemberIds = data ?? [];
        if (segmentMemberIds.length === 0) {
          // Segment matched nothing — short-circuit
          setRows([]);
          setTotalCount(0);
          setError(null);
          setLoading(false);
          return;
        }
      }

      // Step 2: bidder_lifetime_stats with filters + pagination
      let query = supabaseCrm
        .from('bidder_lifetime_stats')
        .select('*', { count: 'exact' })
        .order('last_bid_at', { ascending: false, nullsFirst: false });

      if (statusFilter) query = query.eq('status', statusFilter);
      if (tagFilter) query = query.contains('tenant_tags', [tagFilter]);
      if (hasSegmentFilter) query = query.in('tenant_buyer_id', segmentMemberIds);

      const minSpendCents =
        Number(minSpendDollars) > 0 ? Math.round(Number(minSpendDollars) * 100) : null;
      if (minSpendCents !== null) query = query.gte('total_spend_cents', minSpendCents);

      const minDays = Number(minDaysInactive) > 0 ? Number(minDaysInactive) : null;
      if (minDays !== null) query = query.gte('days_since_last_bid', minDays);

      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, count, error: err } = await query;
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      const bidderRows = ((data ?? []) as unknown as BidderLifetimeStatsRow[]) ?? [];

      // Step 3: fetch profiles for the user_ids on this page
      let nextProfiles = new Map<string, ProfileLite>();
      const userIds = [...new Set(bidderRows.map((r) => r.user_id))];
      if (userIds.length > 0) {
        const { data: profData, error: pErr } = await supabase
          .from('profiles')
          .select('id, display_name, first_name, last_name, phone')
          .in('id', userIds);
        if (!cancelled && !pErr) {
          nextProfiles = new Map(
            ((profData ?? []) as unknown as ProfileLite[]).map((p) => [p.id, p]),
          );
        }
      }

      // Step 4: client-side name search (since email isn't on profiles and
      // PostgREST can't filter joined fields cleanly here)
      let visibleRows = bidderRows;
      const trimmedSearch = search.trim().toLowerCase();
      if (trimmedSearch) {
        visibleRows = bidderRows.filter((row) => {
          const p = nextProfiles.get(row.user_id);
          if (!p) return false;
          const haystack = [p.display_name, p.first_name, p.last_name, p.phone]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(trimmedSearch);
        });
      }

      setRows(visibleRows);
      setProfiles(nextProfiles);
      setTotalCount(count ?? 0);
      setError(null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    statusFilter,
    tagFilter,
    segmentFilter,
    minSpendDollars,
    minDaysInactive,
    search,
    page,
    refreshTick,
  ]);

  // ─── Helpers ────────────────────────────────────────────────────────────
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);
  const statusBadgeVariant = (status: string): 'success' | 'warning' | 'outline' => {
    if (status === 'active') return 'success';
    if (status === 'pending') return 'warning';
    return 'outline';
  };

  const displayNameFor = (row: BidderLifetimeStatsRow): string => {
    const p = profiles.get(row.user_id);
    if (!p) return '—';
    if (p.display_name) return p.display_name;
    const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
    return full || '—';
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Buttons are `whitespace-nowrap`, so without wrapping this row has a
          hard minimum width and pushes the page sideways on a phone. */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Bidders</h1>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Your bidder base — search, tag, segment, and contact.{' '}
            <LearnMoreLink helpSlug="managing-your-bidders">Learn more</LearnMoreLink>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button variant="ghost" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const seg = segments.find((s) => s.id === segmentFilter);
              if (seg) setBroadcastOpen(seg);
            }}
            disabled={!segmentFilter}
            title={segmentFilter ? undefined : 'Select a segment first'}
          >
            ✉ Email segment
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSegmentBuilderOpen('new')}
            disabled={!visibleTenantId}
          >
            + New segment
          </Button>
        </div>
      </header>

      {/* Filters */}
      <section
        aria-label="Filters"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
          alignItems: 'end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Search
          </span>
          <input
            type="search"
            placeholder="Name or phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Status
          </span>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Tag
          </span>
          <select
            value={tagFilter}
            onChange={(e) => {
              setTagFilter(e.target.value);
              setPage(0);
            }}
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          >
            <option value="">Any tag</option>
            {availableTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Segment
          </span>
          <select
            value={segmentFilter}
            onChange={(e) => {
              setSegmentFilter(e.target.value);
              setPage(0);
            }}
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          >
            <option value="">No segment</option>
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Min spend ($)
          </span>
          <input
            type="number"
            min="0"
            value={minSpendDollars}
            onChange={(e) => {
              setMinSpendDollars(e.target.value);
              setPage(0);
            }}
            placeholder="e.g. 100"
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Inactive ≥ N days
          </span>
          <input
            type="number"
            min="0"
            value={minDaysInactive}
            onChange={(e) => {
              setMinDaysInactive(e.target.value);
              setPage(0);
            }}
            placeholder="e.g. 30"
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          />
        </label>
      </section>

      {/* Results */}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {error && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            background: 'var(--surface-alt)',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>
          No bidders match the current filters. Try clearing one.
        </p>
      )}
      {!loading && !error && rows.length > 0 && (
        // Ten columns — the widest table in the app. Without its own scroll
        // container it pushes the whole page sideways on a phone.
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid var(--admin-hairline, var(--border))',
                  textAlign: 'left',
                }}
              >
                <th style={{ padding: '0.5rem', width: '2rem' }}>
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                  />
                </th>
                <th style={{ padding: '0.5rem' }}>#</th>
                <th style={{ padding: '0.5rem' }}>Name</th>
                <th style={{ padding: '0.5rem' }}>Status</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total bids</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total spend</th>
                <th style={{ padding: '0.5rem' }}>Last bid</th>
                <th style={{ padding: '0.5rem' }}>Tags</th>
                <th style={{ padding: '0.5rem' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.tenant_buyer_id}
                  style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}
                >
                  <td style={{ padding: '0.5rem' }}>
                    <input
                      type="checkbox"
                      aria-label={`Select bidder ${row.tenant_bidder_number ?? row.tenant_buyer_id}`}
                      checked={selectedIds.has(row.tenant_buyer_id)}
                      onChange={() => toggleRow(row.tenant_buyer_id)}
                    />
                  </td>
                  <td style={{ padding: '0.5rem' }}>{row.tenant_bidder_number ?? '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{displayNameFor(row)}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.total_bids}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {formatCents(row.total_spend_cents)}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {row.last_bid_at ? `${row.days_since_last_bid ?? 0}d ago` : '—'}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {(row.tenant_tags ?? []).map((tag) => (
                      <Badge key={tag} variant="outline" style={{ marginRight: '0.25rem' }}>
                        {tag}
                      </Badge>
                    ))}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    <Link to={`/admin/bidders/${row.tenant_buyer_id}`}>
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating action bar (visible when ≥1 row selected) */}
      {selectedIds.size > 0 && (
        <div
          style={{
            position: 'sticky',
            bottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 1rem',
            background: 'var(--surface-alt)',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <span style={{ fontSize: '0.875rem' }}>
            {selectedIds.size} selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--brand-accent)',
                cursor: 'pointer',
                marginLeft: '0.5rem',
                fontSize: '0.75rem',
                textDecoration: 'underline',
              }}
            >
              clear
            </button>
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="ghost" size="sm" onClick={() => setBulkTagOpen('add')}>
              Add tag
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setBulkTagOpen('remove')}>
              Remove tag
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {bulkTagOpen && (
        <BulkTagModal
          mode={bulkTagOpen}
          tenantBuyerIds={[...selectedIds]}
          existingTags={availableTags}
          onClose={() => setBulkTagOpen(null)}
          onDone={() => {
            setBulkTagOpen(null);
            setSelectedIds(new Set());
            refresh();
          }}
        />
      )}
      {broadcastOpen && (
        <BroadcastModal
          segment={broadcastOpen}
          onClose={() => setBroadcastOpen(null)}
          onSent={(count) => {
            setBroadcastOpen(null);
            window.alert(`Queued ${count} email${count === 1 ? '' : 's'}.`);
          }}
        />
      )}
      {segmentBuilderOpen && visibleTenantId && session?.user.id && (
        <SegmentBuilderModal
          mode={segmentBuilderOpen}
          tenantId={visibleTenantId}
          userId={session.user.id}
          availableTags={availableTags}
          onClose={() => setSegmentBuilderOpen(null)}
          onSaved={() => {
            setSegmentBuilderOpen(null);
            // Reload segments dropdown
            void (async () => {
              const { data } = await supabaseCrm
                .from('bidder_segments')
                .select('id, tenant_id, name, description, filter_definition')
                .order('name');
              setSegments((data as unknown as BidderSegmentRow[]) ?? []);
            })();
          }}
        />
      )}

      {/* Pagination */}
      {!loading && !error && totalCount > PAGE_SIZE && (
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
          }}
        >
          <span>
            Page {page + 1} of {totalPages} · {totalCount} total
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="ghost"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
}

// ─── Modal sub-components ───────────────────────────────────────────────────

function BulkTagModal({
  mode,
  tenantBuyerIds,
  existingTags,
  onClose,
  onDone,
}: {
  mode: 'add' | 'remove';
  tenantBuyerIds: string[];
  existingTags: string[];
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const trimmed = tag.trim();
    if (!trimmed) {
      setError('Enter a tag.');
      return;
    }
    setBusy(true);
    setError(null);
    const result =
      mode === 'add'
        ? await addTagToBidders(tenantBuyerIds, trimmed)
        : await removeTagFromBidders(tenantBuyerIds, trimmed);
    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    onDone();
  };

  return (
    <ModalShell onClose={onClose}>
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
        {mode === 'add' ? 'Add tag to' : 'Remove tag from'} {tenantBuyerIds.length} bidder
        {tenantBuyerIds.length === 1 ? '' : 's'}
      </h2>
      <p style={{ margin: '0 0 0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        {mode === 'add'
          ? 'Existing bidders with this tag are skipped.'
          : 'Bidders that don’t have this tag are skipped.'}
      </p>
      <input
        type="text"
        list="existing-tags"
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        placeholder="Tag name"
        maxLength={50}
        autoFocus
        style={{
          width: '100%',
          padding: '0.5rem',
          borderRadius: 'var(--r-tile, 0.375rem)',
          border: '1px solid var(--admin-hairline, var(--border))',
        }}
      />
      <datalist id="existing-tags">
        {existingTags.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      {error && (
        <div
          role="alert"
          style={{ color: 'var(--text-danger, #c33)', fontSize: '0.75rem', marginTop: '0.5rem' }}
        >
          {error}
        </div>
      )}
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}
      >
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="default" size="sm" onClick={() => void submit()} disabled={busy}>
          {busy ? 'Applying…' : mode === 'add' ? 'Add tag' : 'Remove tag'}
        </Button>
      </div>
    </ModalShell>
  );
}

interface SegmentFilterDraft {
  statuses: string[];
  tagsAny: string[];
  minSpendDollars: string;
  maxSpendDollars: string;
  minDaysInactive: string;
  minTotalBids: string;
}

function SegmentBuilderModal({
  mode,
  tenantId,
  userId,
  availableTags,
  onClose,
  onSaved,
}: {
  mode: BidderSegmentRow | 'new';
  tenantId: string;
  userId: string;
  availableTags: string[];
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const editing = mode !== 'new';
  const initial: SegmentFilterDraft = useMemo(() => {
    if (editing) {
      const def = mode.filter_definition;
      const fd = (def ?? {}) as Record<string, unknown>;
      return {
        statuses: Array.isArray(fd.status) ? (fd.status as string[]) : [],
        tagsAny: Array.isArray(fd.tags_any) ? (fd.tags_any as string[]) : [],
        minSpendDollars:
          typeof fd.total_spend_cents_gte === 'number'
            ? String(fd.total_spend_cents_gte / 100)
            : '',
        maxSpendDollars:
          typeof fd.total_spend_cents_lte === 'number'
            ? String(fd.total_spend_cents_lte / 100)
            : '',
        minDaysInactive:
          typeof fd.days_since_last_bid_gte === 'number' ? String(fd.days_since_last_bid_gte) : '',
        minTotalBids: typeof fd.total_bids_gte === 'number' ? String(fd.total_bids_gte) : '',
      };
    }
    return {
      statuses: [],
      tagsAny: [],
      minSpendDollars: '',
      maxSpendDollars: '',
      minDaysInactive: '',
      minTotalBids: '',
    };
  }, [editing, mode]);

  const [name, setName] = useState(editing ? mode.name : '');
  const [description, setDescription] = useState<string>(editing ? (mode.description ?? '') : '');
  const [draft, setDraft] = useState<SegmentFilterDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildFilterDefinition = (): Record<string, unknown> => {
    const def: Record<string, unknown> = { version: 1 };
    if (draft.statuses.length > 0) def.status = draft.statuses;
    if (draft.tagsAny.length > 0) def.tags_any = draft.tagsAny;
    if (Number(draft.minSpendDollars) > 0)
      def.total_spend_cents_gte = Math.round(Number(draft.minSpendDollars) * 100);
    if (Number(draft.maxSpendDollars) > 0)
      def.total_spend_cents_lte = Math.round(Number(draft.maxSpendDollars) * 100);
    if (Number(draft.minDaysInactive) > 0)
      def.days_since_last_bid_gte = Number(draft.minDaysInactive);
    if (Number(draft.minTotalBids) > 0) def.total_bids_gte = Number(draft.minTotalBids);
    return def;
  };

  const save = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Segment name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    const result = editing
      ? await updateBidderSegment(mode.id, {
          name: trimmedName,
          description: description.trim() || null,
          filter_definition: buildFilterDefinition() as unknown as Json,
        })
      : await createBidderSegment({
          tenant_id: tenantId,
          name: trimmedName,
          description: description.trim() || null,
          filter_definition: buildFilterDefinition() as unknown as Json,
          created_by: userId,
        });
    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    onSaved();
  };

  const remove = async (): Promise<void> => {
    if (!editing) return;
    if (!confirm(`Delete segment "${mode.name}"?`)) return;
    setBusy(true);
    setError(null);
    const result = await deleteBidderSegment(mode.id);
    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    onSaved();
  };

  const toggleInArray = (list: string[], val: string): string[] =>
    list.includes(val) ? list.filter((x) => x !== val) : [...list, val];

  return (
    <ModalShell onClose={onClose} width="32rem">
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
        {editing ? 'Edit segment' : 'New segment'}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. Top spenders"
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Description (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            style={{
              padding: '0.5rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          />
        </label>

        <fieldset
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-tile, 0.375rem)',
            padding: '0.5rem 0.75rem',
          }}
        >
          <legend style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0 0.25rem' }}>
            Status (none = any)
          </legend>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {STATUSES.map((s) => (
              <label
                key={s}
                style={{
                  display: 'inline-flex',
                  gap: '0.25rem',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={draft.statuses.includes(s)}
                  onChange={() =>
                    setDraft((d) => ({ ...d, statuses: toggleInArray(d.statuses, s) }))
                  }
                />
                {s}
              </label>
            ))}
          </div>
        </fieldset>

        {availableTags.length > 0 && (
          <fieldset
            style={{
              border: '1px solid var(--admin-hairline, var(--border))',
              borderRadius: 'var(--r-tile, 0.375rem)',
              padding: '0.5rem 0.75rem',
            }}
          >
            <legend style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0 0.25rem' }}>
              Has any tag (none = no tag filter)
            </legend>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {availableTags.map((t) => (
                <label
                  key={t}
                  style={{
                    display: 'inline-flex',
                    gap: '0.25rem',
                    alignItems: 'center',
                    fontSize: '0.875rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draft.tagsAny.includes(t)}
                    onChange={() =>
                      setDraft((d) => ({ ...d, tagsAny: toggleInArray(d.tagsAny, t) }))
                    }
                  />
                  {t}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Min spend ($)</span>
            <input
              type="number"
              min="0"
              value={draft.minSpendDollars}
              onChange={(e) => setDraft((d) => ({ ...d, minSpendDollars: e.target.value }))}
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Max spend ($)</span>
            <input
              type="number"
              min="0"
              value={draft.maxSpendDollars}
              onChange={(e) => setDraft((d) => ({ ...d, maxSpendDollars: e.target.value }))}
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Inactive ≥ N days</span>
            <input
              type="number"
              min="0"
              value={draft.minDaysInactive}
              onChange={(e) => setDraft((d) => ({ ...d, minDaysInactive: e.target.value }))}
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Total bids ≥ N</span>
            <input
              type="number"
              min="0"
              value={draft.minTotalBids}
              onChange={(e) => setDraft((d) => ({ ...d, minTotalBids: e.target.value }))}
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
              }}
            />
          </label>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{ color: 'var(--text-danger, #c33)', fontSize: '0.75rem', marginTop: '0.5rem' }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
        <div>
          {editing && (
            <Button variant="ghost" size="sm" onClick={() => void remove()} disabled={busy}>
              Delete
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="default" size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save segment'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function BroadcastModal({
  segment,
  onClose,
  onSent,
}: {
  segment: BidderSegmentRow;
  onClose: () => void;
  onSent: (count: number) => void;
}): React.ReactElement {
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Resolve current member count on open (fresh, not from stale list state)
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await resolveSegmentMemberIds(segment.id);
      if (cancelled) return;
      if (error) {
        setResolveError(error.message);
        return;
      }
      setRecipientCount(data?.length ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [segment.id]);

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && (recipientCount ?? 0) > 0;

  const send = async (): Promise<void> => {
    setSending(true);
    setSendError(null);
    const { data, error } = await broadcastToSegment(segment.id, subject.trim(), body.trim());
    setSending(false);
    if (error) {
      setSendError(error.message);
      return;
    }
    onSent(data ?? 0);
  };

  return (
    <ModalShell onClose={onClose} width="36rem">
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
        Email segment: {segment.name}
      </h2>
      <p style={{ margin: '0 0 0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        {resolveError
          ? `Could not resolve segment: ${resolveError}`
          : recipientCount === null
            ? 'Loading recipient count…'
            : `${recipientCount} recipient${recipientCount === 1 ? '' : 's'} based on the segment's current filter.`}
      </p>

      {!confirming && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                style={{
                  padding: '0.5rem',
                  borderRadius: 'var(--r-tile, 0.375rem)',
                  border: '1px solid var(--admin-hairline, var(--border))',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Body</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder={'Plain text. Blank lines become paragraph breaks.'}
                style={{
                  padding: '0.5rem',
                  borderRadius: 'var(--r-tile, 0.375rem)',
                  border: '1px solid var(--admin-hairline, var(--border))',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
              />
            </label>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setConfirming(true)}
              disabled={!canSend}
            >
              Continue
            </Button>
          </div>
        </>
      )}

      {confirming && (
        <>
          <div
            style={{
              padding: '0.75rem 1rem',
              background: 'var(--surface-alt)',
              border: '1px solid var(--admin-hairline, var(--border))',
              borderRadius: 'var(--r-card, 0.5rem)',
              fontSize: '0.875rem',
            }}
          >
            <strong>
              Send to {recipientCount} bidder{recipientCount === 1 ? '' : 's'}?
            </strong>
            <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted)' }}>
              Each bidder receives one email keyed to your tenant. This can't be unsent.
            </p>
          </div>
          <details style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Preview</summary>
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: 'var(--surface-alt)',
                borderRadius: 'var(--r-tile, 0.375rem)',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{subject}</div>
              <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-muted)' }}>{body}</div>
            </div>
          </details>
          {sendError && (
            <div
              role="alert"
              style={{
                color: 'var(--text-danger, #c33)',
                fontSize: '0.75rem',
                marginTop: '0.5rem',
              }}
            >
              {sendError}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={sending}
            >
              ← Back to edit
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => void send()}
              disabled={sending || !canSend}
            >
              {sending ? 'Sending…' : `Send to ${recipientCount}`}
            </Button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function ModalShell({
  children,
  onClose,
  width = '24rem',
}: {
  children: React.ReactNode;
  onClose: () => void;
  width?: string;
}): React.ReactElement {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          padding: '1.25rem',
          borderRadius: 'var(--r-card, 0.5rem)',
          width,
          maxWidth: 'calc(100vw - 2rem)',
          maxHeight: 'calc(100vh - 2rem)',
          overflow: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}
